import React from 'react';
import PDFSection from 'Views/Pages/Pdf/Components/PDFSection';
import PdfBreak from 'Views/Pages/Pdf/Components/PDFBreak';
import { DataIndex } from 'Util/DataIndexUtils';
import { IGeneratedTermEntityAttributes, StudyPeriodEntity, TermEntity } from 'Models/Entities';
import { IGeneratedPlan, RequisiteWarning } from 'Queries/Hooks/StudyPlan/types';
import StudyPlanTerm from 'Views/Pages/Pdf/StudyPlan/StudyPlanTerm';
import { StyleSheet, Text, View } from '@react-pdf/renderer';

interface StudyPlanYearProps {
	year: string;
	generatedPlan: IGeneratedPlan,
	allStudyPeriods: StudyPeriodEntity[];
	termsGroupedByYear: DataIndex<IGeneratedTermEntityAttributes[]>;
	allTerms: TermEntity[],
	getCourseComponents: (courseId: string) => string[];
	requisiteWarningsByCourseId: DataIndex<RequisiteWarning[]>,
}

const styles = StyleSheet.create({
	periodName: {
		fontWeight: 'bold',
		textTransform: 'uppercase',
	},
	studyPeriod: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
	},
});

const StudyPlanYear = (props: StudyPlanYearProps) => {
	const {
		year,
		generatedPlan,
		allStudyPeriods,
		termsGroupedByYear,
		allTerms,
		getCourseComponents,
		requisiteWarningsByCourseId,
	} = props;

	const generatedTermsForYear = termsGroupedByYear[year];
	const currentYear = generatedTermsForYear[0].year;

	const termsGroupedByPeriod = allStudyPeriods
		.map(studyPeriod => {
			const terms = generatedTermsForYear.filter(term => term.periodOrder === studyPeriod.order);
			terms.sort((termA, termB) => termA.termOrder - termB.termOrder);
			const termIds = terms.map(term => term.id);

			const periodCourses = generatedPlan.plannedCourses
				.filter(
					plannedCourse => termIds.includes(plannedCourse.studyTermId),
				);

			return {
				studyPeriod,
				terms, // terms for this study period
				courses: periodCourses,
			};
		})
		.filter(orderedTerms => orderedTerms.terms.length > 0);

	return (
		<>
			<PDFSection title={`${currentYear}`} key={year}>
				{termsGroupedByPeriod
					.map(({ studyPeriod, terms, courses }) => {
						return (
							<>
								<View style={styles.studyPeriod}>
									<Text style={styles.periodName}>{studyPeriod.name}</Text>
									{
										terms.map(generatedTerm => {
											const term = allTerms
												.find(currentTerm => (
													currentTerm.studyPeriod.order === generatedTerm.periodOrder
													&& currentTerm.order === generatedTerm.termOrder
												));

											const coursesForTerm = courses
												.filter(plannedCourse => plannedCourse.studyTermId === generatedTerm.id)
												.map(plannedCourse => plannedCourse.courseEntity);

											return (
												<StudyPlanTerm
													termName={term?.name || ''}
													courses={coursesForTerm}
													getCourseComponents={getCourseComponents}
													requisiteWarningsByCourseId={requisiteWarningsByCourseId}
												/>
											);
										})
									}
								</View>
								<PdfBreak height={12} />
							</>
						);
					})}
			</PDFSection>
			<PdfBreak />
		</>
	);
};

export default StudyPlanYear;
